import { useRef } from "react";
import { itx } from "../translationItx/itx";
import type Room from "../models/Room";
import type Page from "../models/Page";
import { getChildElement } from "../modelViews/GraficElementUtils";

import Logger from "../Logger";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import BoundaryRect from "../models/BoundaryRect";
import Point from "../models/Point";
import { showToast } from "./Toasts";

const logger = new Logger();

export default function AdjustBackgroundImage(props: { page: Page }) {
  const { page } = props;

  const room = page.parentRoom;
  const initialPos = useRef(page.backgroundImagePosition.copy());
  const initialURL = useRef("");

  useOnChangeWatcher(page.adjustActive);

  let imgElement: HTMLElement | undefined;

  const pageElment = document.getElementById(page.id);
  if (pageElment) {
    imgElement = getChildElement(pageElment, "backgroundImageWrapper") as HTMLElement;
  }

  if (!imgElement || !page.adjustActive.value) {
    return null;
  }

  //
  // slider has values from -100 to +100
  // slider ! scale
  // -100   ! 0.2
  //  0     ! 1
  // +100   ! 5
  //

  function scaleToSliderValue(scale: number): number {
    let res = 0;
    if (scale >= 1) {
      res = (scale - 1) / 4;
    } else {
      res = (-(1 / scale) + 1) / 4;
    }

    return res * 100;
  }

  function sliderToScaleValue(sliderValue: number): number {
    var v = sliderValue / 100;

    if (v < 0) {
      v = -v;
      v = 1 / (v * 4 + 1);
    } else if (v > 0) {
      v = 4 * v + 1;
    } else {
      v = 1;
    }

    v = parseFloat(v.toFixed(2));
    return v;
  }

  function FadeElement(props: {}) {
    const fadeInput = useRef<HTMLInputElement>(null);
    const fadeSlider = useRef<HTMLInputElement>(null);

    function onFade(ev: any) {
      let fade = ev.target.value;
      if (fade < 0) {
        fade = 0;
      }

      if (fade > 80) {
        fade = 80;
      }

      page.backgroundImagePosition.fade = fade;
      page.raiseOnChange("backgroundImagePosition");

      if (fadeInput.current) {
        fadeInput.current.value = fade;
      }
      if (fadeSlider.current) {
        fadeSlider.current.value = fade;
      }
    }
    function setFadeForAll(ev: any) {
      const fade = page.backgroundImagePosition.fade;
      state.floors.forEach((floor) => {
        const floorPageBackgroundImagePosition = floor.getOrCreatePage().backgroundImagePosition;
        floorPageBackgroundImagePosition.fade = fade;
        floor.rooms.forEach((room) => {
          if (!room.isFloorGeneralRoom) {
            room.getOrCreatePage().backgroundImagePosition.fade = fade;
          }
        });
      });
      state.settings.defaultBackgroundPictureElements.fade = fade;
      showToast({ text: itx("AdjustBackgroundImage.fadeSet", "Transparenz wurde für alle Seiten übernommen.") });
      ///////////////////////////////////////////
    }

    return (
      <div style={{ width: "100%", paddingBottom: "10px" }}>
        <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
          <img src="/images/fade.png" style={{ width: "26px", height: "26px", marginLeft: "0px" }} />
          <div style={{ marginLeft: "5px" }}>{itx("SettingsScreen.Fade", "Fade")}</div>

          <input
            className="input"
            ref={fadeInput}
            type="numeic"
            min={0}
            max={80}
            defaultValue={page.backgroundImagePosition.fade}
            onChange={onFade}
            style={{ width: "50px", height: "21px", position: "absolute", right: "38px", marginTop: "3px" }}
          />
        </div>
        <div style={{ width: "100%" }}>
          <input
            ref={fadeSlider}
            style={{ width: "100%" }}
            type="range"
            min={0}
            max={80}
            defaultValue={page.backgroundImagePosition.fade}
            onChange={onFade}
          />
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            fontSize: "15px",
            alignContent: "center",
            justifyItems: "center",
          }}
        >
          <div style={{ paddingBottom: "5px", width: "26px", height: "26px" }}></div>
          <div style={{}}>{itx("AdjustBackgroundImage.setForAll", "Für Alle übernehmen")}</div>
          <div style={{ paddingBottom: "5px", marginLeft: "5px" }}>
            <button
              style={{ position: "absolute", right: "50px", backgroundColor: "var(--whiteHalfTransparancy)" }}
              className="iconButton"
              onClick={setFadeForAll}
            >
              <img
                src="/images/applyToAll.png" // no itx
                style={{ width: "20px", height: "20px", marginLeft: "0px", padding: "1px" }}
              />
            </button>
          </div>
        </div>
      </div>
    );
  }

  function ScaleElement(props: {}) {
    const scaleInput = useRef<HTMLInputElement>(null);
    const scaleSlider = useRef<HTMLInputElement>(null);
    //
    // MWE 11.1.2025
    // leider funktioniert zoomen der BackgrundImage mit Floorplans zur Zeit so gar nicht !!!!!!! :-(((((
    // daher bis auf weiteres toto gelegt
    //
    if (page.isBackgroundImageFromFloorPlan) {
      return null;
    }

    useOnChangeWatcher(page, (changedProperty, changedObj) => {
      return updateControls();
    });
    useOnChangeWatcher(page.parentRoom?.roomtoFloorDrawingTransform);

    function updateControls() {
      if (scaleInput.current) {
        scaleInput.current.value = page.backgroundImagePosition.scale.toFixed(2);
      }
      if (scaleSlider.current) {
        scaleSlider.current.value = `${scaleToSliderValue(page.backgroundImagePosition.scale)}`;
      }
      return false;
    }
    function onScale(ev: any) {
      let scale = ev.target.value;
      if (scale < 0.1) {
        scale = 0.1;
      }

      if (scale > 5) {
        scale = 5;
      }

      page.backgroundImagePosition.scale = scale;
      page.raiseOnChange("backgroundImagePosition");

      if (scaleInput.current) {
        scaleInput.current.value = scale;
      }
      if (scaleSlider.current) {
        scaleSlider.current.value = scale;
      }
    }

    function onChangeScaleSlider(ev: any) {
      var vOrg = parseFloat(ev.target.value);
      const v = sliderToScaleValue(vOrg);

      if (page.isBackgroundImageFromFloorPlan) {
        const room = page.parent as Room;
        if (page.isBackgroundImageFromFloorPlan && room?.roomtoFloorDrawingTransform) {
          const boundingRect = room.boundingRectInFloorSpace;

          const oldTrans = room.roomtoFloorDrawingTransform.copy();

          const newScale = 1 / v;
          const oldScale = room.roomtoFloorDrawingTransform.scale;

          let dx = (boundingRect.width * (v - oldScale)) / 2;
          let dy = (boundingRect.height * (v - oldScale)) / 2;

          // dx = 0;
          // dy = 0;

          let newBoundaryRect = new BoundaryRect(
            new Point(boundingRect.min.x - dx, boundingRect.min.y - dy),

            new Point(boundingRect.max.x + dx, boundingRect.max.y + dy),
          );

          page.freezeFloorPosition;
          room.roomtoFloorDrawingTransform.sizeAndPositionFromBoundingRectWithBorder(newBoundaryRect, page);
          page.unfreezeFloorPosition();

          page.backgroundImagePosition.posX += dx;
          page.backgroundImagePosition.posY += dy;

          room.raiseOnChange("boundingRectInFloorSpace");
          room.roomtoFloorDrawingTransform.raiseOnChange();
          page.raiseOnChange("backgroundImagePosition");

          const nextScale = room.roomtoFloorDrawingTransform.scale;
          ev.target.value = scaleToSliderValue(1 / nextScale).toString();

          //                    page.setNewScaleForBackgroundImageFromFloorplan(1 / v)
        }
      } else {
        page.backgroundImagePosition.scale = v;
        page.raiseOnChange("backgroundImagePosition");
      }
      updateControls();
    }

    function onChangeScaleInput(ev: any, scale: boolean) {
      var v = parseFloat(ev.target.value);
      if (v >= 0.2 && 5 >= v) {
        var v = v;

        if (page.isBackgroundImageFromFloorPlan) {
          const room = page.parent as Room;
          if (room.roomtoFloorDrawingTransform) {
            room.roomtoFloorDrawingTransform.scale = 1 / v;
            room.roomtoFloorDrawingTransform.raiseOnChange();
          }
        } else {
          page.backgroundImagePosition.scale = v;
          page.raiseOnChange("backgroundImagePosition");
        }
      }
      updateControls();
    }

    return (
      <div style={{ width: "100%", paddingBottom: "10px" }}>
        <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
          <img src="/images/scale.png" style={{ width: "26px", height: "26px", marginLeft: "0px" }} />
          <div style={{ marginLeft: "5px" }}>{itx("PageView.scale", "Scale")}</div>
          <input
            className="input"
            ref={scaleInput}
            type="numeic"
            min={0.1}
            max={5}
            defaultValue={page.backgroundImagePosition.scale.toFixed(2)}
            onBlur={(ev) => {
              onChangeScaleInput(ev, false);
            }}
            // onChange={onScale}
            style={{ width: "50px", height: "21px", position: "absolute", right: "38px", marginTop: "3px" }}
          />
        </div>
        <div style={{ width: "100%" }}>
          <input
            type="range"
            ref={scaleSlider}
            style={{ width: "100%" }}
            min={-100}
            max={100}
            defaultValue={scaleToSliderValue(page.backgroundImagePosition.scale)}
            onChange={onChangeScaleSlider}
            // defaultValue={page.backgroundImagePosition.scale}
            // onChange={onScale}
          />
        </div>
      </div>
    );
  }

  function RotateElement(props: {}) {
    async function rotateBackgroundClockwise(degre?: number) {
      try {
        if (degre == 0) {
          return;
        }
        const base64data = rotateBase64Image90deg(page.backgroundImgUrl, degre);
        page.backgroundImgUrl = base64data;
        page.raiseOnChange("backgroundImgUrl");
      } catch (ex) {
        logger.error(ex);
      }
    }

    function rotateBase64Image90deg(base64Image: string, deg?: number): string {
      // from https://stackoverflow.com/questions/17040360/javascript-function-to-rotate-a-base-64-image-by-x-degrees-and-return-new-base64
      // create an off-screen canvas
      var canvasOffScreen = document.createElement("canvas");
      var canvasOffScreenContext = canvasOffScreen.getContext("2d");
      let degree = deg || 90;

      // cteate Image
      var image = new Image();
      image.src = base64Image;

      // set its dimension to rotated size
      canvasOffScreen.height = degree % 180 === 0 ? image.height : image.width;
      canvasOffScreen.width = degree % 180 === 0 ? image.width : image.height;
      if (canvasOffScreenContext) {
        // rotate and draw  image in off-screen canvas:
        canvasOffScreenContext.translate(canvasOffScreen.width / 2, canvasOffScreen.height / 2);
        canvasOffScreenContext.rotate((degree * Math.PI) / 180);
        canvasOffScreenContext.drawImage(image, image.width / -2, image.height / -2);
      }
      // encode image to data-uri with base64
      return canvasOffScreen.toDataURL("image/jpeg", 100);
    }

    if (page.isBackgroundImageFromFloorPlan) {
      return null;
    }
    return (
      <div style={{ width: "100%", paddingBottom: "10px", height: "37px" }}>
        <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
          <img src="/images/rotate.png" style={{ width: "26px", height: "26px", marginLeft: "0px" }} />
          <div style={{ marginLeft: "5px" }}>{state.translate("PageView.rotateClockWise", "Drehen")}</div>
          <button
            style={{ position: "absolute", right: "50px", backgroundColor: "var(--whiteHalfTransparancy)" }}
            className="iconButton"
            onClick={() => {
              rotateBackgroundClockwise(90);
              page.backgroundImagePosition.rotation = (page.backgroundImagePosition.rotation + 90) % 360;
            }}
          >
            <img
              src="/images/drehen.png" // no itx
              style={{ width: "15px", height: "15px", marginLeft: "0px", padding: "1px", transform: "scaleX(-1)" }}
            />
          </button>
        </div>
      </div>
    );
  }

  function DeleteBackgroundButton(props: {}) {
    if (page.isBackgroundImageFromFloorPlan) {
      return null;
    }
    return (
      <button
        style={{
          backgroundColor: "var(--whiteHalfTransparancy)",
          width: "100%",
          paddingBottom: "10px",
        }}
        className="button"
        onClick={() => {
          page.adjustActive.value = false;
          page.backgroundImgUrl = "";
          page.raiseOnChange("backgroundImgUrl");
        }}
      >
        <i className="las la-trash" /> {state.translate("PageView.deleteBackground", "Hintergrund entfernen")}
      </button>
    );
  }

  function onCancle(ev: any) {
    //
    // MWE 11.1.2025:
    // cancel not working !!!
    //

    // page.backgroundImagePosition = initialPos.current;
    page.raiseOnChange("backgroundImagePosition");

    if (room?.roomtoFloorDrawingTransform) {
      //            room.roomtoFloorDrawingTransform.raiseOnChange();
    }

    page.adjustActive.value = false;
  }

  function onTouch(event: any) {
    event.stopPropagation();
    console.log("touch event", event);
  }

  return (
    <div
      style={{
        position: "absolute",
        display: "flex",
        flexDirection: "column",
        left: "0px",
        top: "0px",
        height: "max-content",
        backgroundColor: "var(--adjustBluePartTransparent)",
        margin: "10px",
        borderRadius: "5px",
        overflow: "hidden",
        boxShadow: "var(--panel-shadow)",
      }}
      onTouchStart={onTouch}
      onTouchMove={onTouch}
      onTouchEnd={onTouch}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          backgroundColor: "var(--adjustBlueHeader)",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            padding: "5px",
            textAlign: "center",
            width: "100%",
          }}
        >
          {state.translate("PageView.moveScaleBackground", "Verschiebe und Skaliere die Hintergrundzeichnung")}
        </div>
        <div
          className="dialogHeaderButton"
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onPointerMove={(event) => {
            event.stopPropagation();
          }}
          onPointerEnter={(event) => {
            event.stopPropagation();
          }}
          onClick={onCancle}
        >
          <i className="las la-times" />
        </div>
      </div>

      <br />
      <div
        style={{
          padding: "20px",
          paddingTop: "0px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          {!page.isBackgroundImageFromFloorPlan && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <FadeElement />
              <ScaleElement />
              <RotateElement />
              <DeleteBackgroundButton />
            </div>
          )}

          {page.isBackgroundImageFromFloorPlan && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              {itx("AdjustBackgroundImage.verschiebedenhintergrund", "Verschiebe den Hintergrund,")} <br />
              {itx("AdjustBackgroundImage.indemduihn", "indem Du ihn anklickst und ziehst.")}
            </div>
          )}

          <div style={{ borderLeft: "1px solid grey", height: "100%", marginLeft: "20px" }} />
        </div>
      </div>
    </div>
  );
}
